// React Imports
import { ReactElement } from "react";

// Third Party Libraries Imports
import { Checkbox, DatePicker, Form, Input, Select, Switch } from "antd";
import { PhoneInput } from "react-international-phone";
import dayjs from "dayjs";

// Local Imports
import Label from "../../../components/label/label.component";
import CustomInput from "../../../components/custom-input/custom-input.component";
import { CustomerStatus } from "../../../models/enum";
import CustomerService from "../customer.service";

// Style Imports
import "./edit-customer-form.scss";
import User from "../../../models/user";

interface EditCustomerProps {
  selectedUser: User | undefined;
  onSuccess: () => void;
  onCancel: () => void;
}

const EditCustomerForm: React.FC<EditCustomerProps> = ({
  selectedUser,
  onSuccess,
  onCancel,
}: EditCustomerProps): ReactElement => {
  const customerService = new CustomerService();
  const [form] = Form.useForm();

  const onSubmit = async (values: User) => {
    values.id = selectedUser?.id as number;
    console.log(values);

    if (values?.unlockAccount) {
      await customerService?.forceUpdatePassword(selectedUser?.id);
      values.status = "password_expired";
    }

    // delete values.unlockAccount;

    const response: boolean = await customerService?.updateCustomerData(values);
    if (response) {
      onSuccess();
    }
  };

  return (
    <div className="edit-customer-form-container flex flex-col">
      <h3 className=" text-black-500 text-lg md:text-2xl font-semibold tracking-[0.24px] m-0 px-5 md:px-10">
        Edit customer
      </h3>
      <p className=" text-blue-200 text-sm md:text-base font-medium tracking-[0.16px] px-5 md:px-10">
        Please fill out the following fields to create a new user
      </p>
      <Form
        form={form}
        onFinish={onSubmit}
        scrollToFirstError={true}
        initialValues={{
          firstName: selectedUser?.firstName,
          lastName: selectedUser?.lastName,
          phoneNumber: selectedUser?.phoneNumber,
          email: selectedUser?.email,
          status: selectedUser?.status,
          emailVerified: selectedUser?.emailVerified,
          phoneVerified: selectedUser?.phoneVerified,
          forceResetPassword: selectedUser?.forceResetPassword || null,
          unlockAccount: false,
          lockoutUntil: selectedUser?.lockoutUntil
            ? dayjs(selectedUser?.lockoutUntil)
            : null,
        }}
      >
        <div className="flex flex-wrap justify-between border-t-[1px] border-gray-100 mt-5 px-5 md:px-10">
          <div className="flex flex-col w-full md:w-[48%] pt-5">
            <Label text="First name" />
            <Form.Item
              name="firstName"
              rules={[
                {
                  required: true,
                  message: "Please input your first name!",
                },
              ]}
            >
              <CustomInput placeholder="Enter first name" />
            </Form.Item>
          </div>
          <div className="flex flex-col w-full md:w-[48%] pt-0 md:pt-5">
            <Label text="Last name" />
            <Form.Item
              name="lastName"
              rules={[
                {
                  required: true,
                  message: "Please input your last name!",
                },
              ]}
            >
              <CustomInput placeholder="Enter last name" />
            </Form.Item>
          </div>
          <div className="flex flex-col w-full md:w-[48%]">
            <Label text="Mobile number" />
            <Form.Item
              name="phoneNumber"
              rules={[
                {
                  required: true,
                  message: "Please input your mobile number!",
                },
              ]}
            >
              <div className="phone-input">
                <PhoneInput
                  placeholder="Enter mobile number"
                  value={
                    (selectedUser?.phoneNumber as unknown as
                      | string
                      | undefined) || undefined
                  }
                  defaultCountry="kw"
                />
              </div>
            </Form.Item>
          </div>
          <div className="flex flex-col w-full md:w-[48%]">
            <Label text="Email" />
            <Form.Item
              name="email"
              rules={[
                {
                  required: true,
                  message: "Please input your email!",
                },
                {
                  type: "email",
                  message: "Please enter valid email!",
                },
              ]}
            >
              <CustomInput placeholder="Enter email" />
            </Form.Item>
          </div>
          <div className="field flex flex-col w-full md:w-[48%]">
            <Label text="Status" />
            <Form.Item
              name="status"
              rules={[
                {
                  required: true,
                  message: "Please input your email!",
                },
              ]}
            >
              <Select
                placeholder="Select status"
                options={[
                  {
                    value: CustomerStatus.Verified,
                    label: CustomerStatus.Verified,
                  },
                  {
                    value: CustomerStatus?.Pending,
                    label: CustomerStatus?.Pending,
                  },
                ]}
              />
            </Form.Item>
          </div>
          <div className="field flex flex-col w-full md:w-[48%]">
            <div className="flex items-center gap-2">
              <Checkbox
                defaultChecked={selectedUser?.lockoutUntil ? true : false}
                className="pb-1"
                onChange={(e) => {
                  form.setFieldsValue({
                    lockoutUntil: e.target.checked ? dayjs() : null,
                  });
                }}
              />
              <Label text="Lock until" />
            </div>
            <Form.Item name="lockoutUntil">
              <DatePicker
                showTime
                showNow={false}
                allowClear={true}
                format="YYYY-MM-DD HH:mm:ss"
                disabledDate={(current: any) => {
                  return current && current < Date.now();
                }}
              />
            </Form.Item>
          </div>
          <div className="flex justify-between flex-wrap w-full">
            <div className="flex flex-col w-full md:w-[48%]">
              <Label text="Password" />
              <Form.Item name="password">
                <Input.Password
                  className="input-field-container"
                  type="password"
                  placeholder="Enter Password"
                  autoComplete="new-password"
                />
              </Form.Item>
            </div>
            <div className="flex flex-col w-full md:w-[48%]">
              <Label text="Confirm password" />
              <Form.Item
                name="Confirm password"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          "The new password that you entered do not match!"
                        )
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  className="input-field-container"
                  type="password"
                  placeholder="Enter Confirm Password"
                  autoComplete="new-password"
                />
              </Form.Item>
            </div>
          </div>
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col w-[48%]">
              <Form.Item name="unlockAccount">
                <Checkbox defaultChecked={selectedUser?.unlockAccount} onChange={(e) => {
                  form.setFieldsValue({
                    unlockAccount: e.target.checked,
                  });
                }} />
                <span className="ps-3 font-medium text-sm">
                  Reset to first use
                </span>
              </Form.Item>
            </div>
            <div className="flex items-center justify-between w-full md:w-[48%]">
              <div className="flex flex-col w-[48%]">
                <Label text="Mobile confirmed" style={{ fontSize: "12px" }} />
                <Form.Item
                  name="phoneVerified"
                  initialValue={selectedUser?.phoneVerified}
                >
                  <Switch defaultChecked={selectedUser?.phoneVerified} />
                </Form.Item>
              </div>
              <div className="flex flex-col w-[48%]">
                <Label text="Email confirmed" style={{ fontSize: "12px" }} />
                <Form.Item name="emailVerified">
                  <Switch defaultChecked={selectedUser?.emailVerified} />
                </Form.Item>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-flex-end w-full mt-3 pt-7 px-5 md:px-10 border-t-[1px] border-gray-100">
          <div className="flex justify-between items-center w-full md:w-[48%]">
            <button
              onClick={onCancel}
              className="border-[1px] border-blue-500 bg-transparent text-blue-500 rounded-[8px] py-3 w-[49%]"
              type="button"
            >
              Cancel
            </button>
            <button className="border-blue-500 bg-blue-500 text-white rounded-[8px] py-3 w-[49%]">
              Confirm
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default EditCustomerForm;
